import { useState } from "react";
import { Loader2, Check, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", company: "", message: "" };

export function TalkToSalesDialog({ open, onOpenChange, plan = "" }) {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => { setForm(EMPTY); setState("idle"); setError(""); }, 200);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      const res = await fetch(`${API_BASE}/leads/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, plan, page_url: window.location.href }),
      });
      if (!res.ok) throw new Error(res.status === 429
        ? "Too many enquiries from this network. Please try again later."
        : "We could not send that. Please email corporate@yanthralabs.com.");
      setState("done");
    } catch (err) {
      setError(err.message || "Something went wrong.");
      setState("idle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[480px] rounded-2xl" data-testid="talk-to-sales-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {state === "done" ? "Thank you — we'll be in touch" : "Talk to sales"}
          </DialogTitle>
          <DialogDescription>
            {state === "done"
              ? "A specialist from Yanthra Labs will reach out within one business day."
              : plan
                ? `Tell us about your team and we'll scope ${plan} for you.`
                : "Tell us about your team and we'll come back with a plan that fits."}
          </DialogDescription>
        </DialogHeader>

        {state === "done" ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-secondary/40 p-4"
               data-testid="talk-to-sales-success">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[hsl(var(--ink-success))]/12">
              <Check className="w-4 h-4" style={{ color: "hsl(var(--ink-success))" }} strokeWidth={3} />
            </span>
            <p className="text-sm text-foreground/80">
              Your enquiry is logged. You can close this window.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-3">
            <Input required placeholder="Full name" value={form.name}
                   onChange={set("name")} data-testid="sales-name-input" />
            <Input required type="email" placeholder="Work email" value={form.email}
                   onChange={set("email")} data-testid="sales-email-input" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Phone (optional)" value={form.phone}
                     onChange={set("phone")} data-testid="sales-phone-input" />
              <Input placeholder="Company" value={form.company}
                     onChange={set("company")} data-testid="sales-company-input" />
            </div>
            <Textarea rows={3} placeholder="How many people, and what do you need?"
                      value={form.message} onChange={set("message")}
                      data-testid="sales-message-input" />
            {error && (
              <p className="text-sm" style={{ color: "hsl(var(--ink-danger))" }}
                 data-testid="sales-error">{error}</p>
            )}
            <Button type="submit" disabled={state === "sending"} className="rounded-full h-11 font-bold"
                    data-testid="sales-submit-btn">
              {state === "sending"
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
                : (<>Send enquiry <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} /></>)}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TalkToSalesDialog;
